import Spinner from "@/components/Spinner";
import { PhotoIcon, TrashIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import Image from "next/legacy/image";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

type ImageUploaderS3Props = {
  noteUid: string;
  allowMultipleImages: boolean;
  isDisabled: boolean;
  onImagesChange: (urls: string[]) => void;
  initialImageUrls?: string[];
};

const ImageUploaderS3 = ({
  noteUid,
  allowMultipleImages,
  isDisabled,
  onImagesChange,
  initialImageUrls = [],
}: ImageUploaderS3Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageUrls, setImageUrls] = useState<string[]>(initialImageUrls);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingIndices, setDeletingIndices] = useState<number[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setImageUrls(initialImageUrls);
  }, [initialImageUrls]);

  const uploadImage = async (file: File) => {
    const filename = `notes/${noteUid}/${uuidv4().slice(0, 8)}.${file.name
      .split(".")
      .pop()}`;
    try {
      setIsUploading(true);

      // Get presigned URL
      const presignedResponse = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/images/s3/presigned-upload`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filename, contentType: file.type }),
        }
      );

      if (!presignedResponse.ok) {
        throw new Error("Failed to get presigned URL");
      }

      const { presignedUrl, formFields, fileKey, bucketUrl } =
        await presignedResponse.json();

      // Prepare form data for upload
      const formData = new FormData();
      Object.entries(formFields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append("file", file);

      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: "POST",
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload image to S3");
      }

      const imageUrl = `${bucketUrl}/${fileKey}`;
      return imageUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      setError("Failed to upload image. Please try again.");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    setError("");
    setIsUploading(true);

    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        setError("Please select only image files.");
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError("Each file size should be less than 10MB.");
        return false;
      }
      return true;
    });

    if (!allowMultipleImages && validFiles.length > 1) {
      setError("Only one image is allowed.");
      setIsUploading(false);
      return;
    }

    const newUrls = [];
    for (const file of validFiles) {
      const url = await uploadImage(file);
      if (url) newUrls.push(url);
    }

    const updatedUrls = allowMultipleImages
      ? [...imageUrls, ...newUrls]
      : newUrls;
    setImageUrls(updatedUrls);
    onImagesChange(updatedUrls);

    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = async (index: number) => {
    const imageToRemove = imageUrls[index];
    if (imageToRemove) {
      setDeletingIndices((prev) => [...prev, index]);
      try {
        // Extract the key from the URL
        const key = new URL(imageToRemove).pathname.slice(1); // Remove leading '/'

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/images/s3/delete`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete image");
        }
        console.log(`Deleted image: ${imageToRemove}`);

        const updatedUrls = imageUrls.filter((_, i) => i !== index);
        setImageUrls(updatedUrls);
        onImagesChange(updatedUrls);
      } catch (error) {
        console.error("Error deleting file:", error);
        setError(`Failed to delete image: ${error.message}`);
      } finally {
        setDeletingIndices((prev) => prev.filter((i) => i !== index));
      }
    }
  };

  return (
    <div className={clsx("flex w-full max-w-28 grow-0 p-4 lg:w-1/6 lg:p-2")}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className={clsx("hidden")}
        multiple={allowMultipleImages}
        disabled={isDisabled}
      />

      <div className={clsx("flex w-full flex-col space-y-2")}>
        {imageUrls.map((url, index) => {
          const isDeleting = deletingIndices.includes(index);

          return (
            <div
              key={index}
              className={clsx("relative overflow-hidden rounded-lg")}
            >
              <div className={clsx("relative aspect-square")}>
                <Image
                  src={url}
                  alt={`File preview ${index + 1}`}
                  layout="fill"
                  className={clsx("object-cover")}
                />
              </div>
              {!isDeleting && (
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className={clsx(
                    "absolute right-2 top-2 flex size-6 items-center justify-center rounded-full bg-zinc-950 text-zinc-50 hover:bg-zinc-800"
                  )}
                >
                  <TrashIcon className={clsx("size-4")} />
                </button>
              )}
              {isDeleting && (
                <div
                  className={clsx(
                    "absolute inset-0 flex items-center justify-center rounded-lg bg-zinc-800 bg-opacity-50 text-white"
                  )}
                >
                  <Spinner size="sm" />
                </div>
              )}
            </div>
          );
        })}
        {(allowMultipleImages || imageUrls.length === 0) && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={clsx("relative flex w-full", {
              "pointer-events-none opacity-50": isDisabled,
            })}
            disabled={isDisabled}
          >
            <div
              className={clsx(
                "relative flex aspect-square w-full items-center justify-center rounded-lg border border-dashed border-zinc-300 transition ease-out hover:border-zinc-400 hover:bg-zinc-100"
              )}
            >
              <PhotoIcon className={clsx("size-5 text-zinc-500")} />
            </div>
          </button>
        )}
      </div>
      {error && (
        <div className={clsx("mt-2 font-body text-sm text-red-500")}>
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUploaderS3;
